import { flowRight } from 'lodash';
import React from 'react';
import I18n from 'i18n-js';
import bind from '@reverbdotcom/commons/src/bind';
import gql from 'graphql-tag';
import { MutationOpts } from 'react-apollo';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import {
  ApolloQueryResult,
} from 'apollo-client';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import {
  core_apimessages_Channel,
  UpdateFeedback,
} from '../../schemas/rql';
import FormGroup from '@reverbdotcom/commons/src/components/form_group';
import FormGroupWithInput from '@reverbdotcom/commons/src/components/form_group_with_input';
import FormGroupWithRadio from '@reverbdotcom/commons/src/components/form_group_with_radio';
import { RCStarRatingSelector } from '@reverbdotcom/commons/src/rc/RCStarRatingSelector';
import { parseGraphqlErrors } from '@reverbdotcom/commons/src/parse_graphql_errors';
import URLHelpers from '../shared/url_helpers';
import toBool from '../../reverb-js-common/to_boolean';
import { IUserContext, withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { translateElement } from '@reverbdotcom/commons/src/html_translation';
import { RCAlertBox } from '@reverbdotcom/commons/src/rc/RCAlertBox';

const updateFeedbackGQL = gql`
  mutation UpdateFeedback($input: Input_core_apimessages_UpdateFeedbackRequest) {
    updateFeedbackRequest(input: $input) {
      feedback {
        rating
        shippingSatisfactory {
          value
        }
        conditionSatisfactory {
          value
        }
        message
      }
    }
  }
`;

const updateFeedbackMutation = withGraphql<
{},
UpdateFeedback.Mutation,
UpdateFeedback.Variables
>(updateFeedbackGQL, {
  name: 'updateFeedbackMutation',
});

interface IRQLProps {
  updateFeedbackMutation: (opts: MutationOpts) => Promise<ApolloQueryResult<UpdateFeedback.UpdateFeedbackRequest>>;
}

interface IOwnProps {
  orderUuid: string;
  legacyOrderId: string;
  onSuccess: Function;
  onError: Function;
  isBuyer: boolean;
  channel?: core_apimessages_Channel;
}

type IProps = IRQLProps & IUserContext & IOwnProps;

interface IState {
  rating: number;
  message: string;
  shippingSatisfactory: boolean;
  conditionSatisfactory: boolean;
  submitDisabled: boolean;
}

export class FeedbackForm extends React.Component<IProps, IState> {
  state = {
    message: '',
    rating: null,
    shippingSatisfactory: null,
    conditionSatisfactory: null,
    submitDisabled: false,
  };

  get isLp() {
    return this.props.channel === core_apimessages_Channel.LP;
  }

  showStructuredFeedback() {
    return this.isLp && this.props.isBuyer;
  }

  shippingSatisfactoryValue() {
    if (this.state.shippingSatisfactory === null) { return null; }
    return {
      value: this.state.shippingSatisfactory,
    };
  }

  conditionSatisfactoryValue() {
    if (this.state.conditionSatisfactory === null) { return null; }
    return {
      value: this.state.conditionSatisfactory,
    };
  }

  @bind
  createFeedback() {
    this.setState({ submitDisabled: true });

    return this.props.updateFeedbackMutation({
      variables: {
        input: {
          orderUuid: this.props.orderUuid,
          rating: this.state.rating,
          message: this.state.message,
          shippingSatisfactory: this.shippingSatisfactoryValue(),
          conditionSatisfactory: this.conditionSatisfactoryValue(),
        },
      },
    }).then(this.onSuccess, this.onError);
  }

  @bind
  onSuccess() {
    this.props.onSuccess();
  }

  @bind
  onError(errorResponse) {
    this.setState({ submitDisabled: false });
    this.props.onError(
      parseGraphqlErrors(errorResponse),
    );
  }

  @bind
  onValueChange(obj) {
    this.setState(obj);
  }

  @bind
  starRatingSelected(starsSelected) {
    this.setState({ rating: starsSelected });
  }

  @bind
  updateShipping(obj) {
    this.setState({ shippingSatisfactory: toBool(obj.shippingSatisfactory) });
  }

  @bind
  updateCondition(obj) {
    this.setState({ conditionSatisfactory: toBool(obj.conditionSatisfactory) });
  }

  @bind
  formValid() {
    if (this.state.rating === null) { return false; }
    if (
      this.showStructuredFeedback() &&
      (this.state.shippingSatisfactory === null || this.state.conditionSatisfactory === null)
    ) {
      return false;
    }
    return true;
  }

  showStartAConversation() {
    return this.state.rating && this.state.rating > 0 && this.state.rating < 4;
  }

  buyerSuggestion() {
    const msgUrl = URLHelpers.purchasePath({
      orderId: this.props.legacyOrderId,
    });
    const refundUrl = URLHelpers.purchasePath({
      orderId: this.props.legacyOrderId,
      actions_expanded: true,
    });

    return (
      <div>
        <SanitizedRender
          html={translateElement('b', 'discovery.feedbackForm.problemsWithYourOrder')}
        />
        <br />
        <SanitizedRender
          html={
            I18n.t('discovery.feedbackForm.buyerSuggestion', {
              msgLinkOpen: `<a href="${msgUrl}">`,
              msgLinkClose: '</a>',
              refundLinkOpen: `<a href="${refundUrl}">`,
              refundLinkClose: '</a>',
            })
          }
        />
      </div>
    );
  }

  sellerSuggestion() {
    const url = URLHelpers.orderPath({
      orderId: this.props.legacyOrderId,
    });

    return (
      <div>
        <SanitizedRender
          html={translateElement('b', 'discovery.feedbackForm.problemsWithYourOrder')}
        />
        <br />
        <SanitizedRender
          html={
            I18n.t('discovery.feedbackForm.sellerSuggestion', {
              linkOpen: `<a href="${url}">`,
              linkClose: '</a>',
            })
          }
        />
      </div>
    );
  }

  renderStartAConversation() {
    return (
      <div className="mb-1" data-start-conversation>
        <RCAlertBox type="info" small>
          <div className="mb-0">
            {this.props.isBuyer && this.buyerSuggestion()}
            {!this.props.isBuyer && this.sellerSuggestion()}
          </div>
        </RCAlertBox>
      </div>
    );
  }

  renderStructuredFeedback() {
    return (
      <div>
        <div className="mb-2" data-structured-feedback>
          <FormGroup
            label={I18n.t('discovery.feedbackForm.shippingLabel')}
          >
            <div className="d-inline-block mr-2">
              <FormGroupWithRadio
                label={I18n.t('discovery.feedbackForm.shippingSatisfactory')}
                fieldName="shippingSatisfactory"
                inputName={`shippingSatisfactory-${this.props.orderUuid}`}
                value="true"
                updateField={this.updateShipping}
                checked={this.state.shippingSatisfactory === true}
              />
            </div>
            <div className="d-inline-block">
              <FormGroupWithRadio
                label={I18n.t('discovery.feedbackForm.shippingNotSatisfactory')}
                fieldName="shippingSatisfactory"
                inputName={`shippingNotSatisfactory-${this.props.orderUuid}`}
                value="false"
                updateField={this.updateShipping}
                checked={this.state.shippingSatisfactory === false}
              />
            </div>
          </FormGroup>
        </div>
        <div className="mb-2">
          <FormGroup
            label={I18n.t('discovery.feedbackForm.conditionLabel')}
          >
            <div className="d-inline-block mr-2">
              <FormGroupWithRadio
                label={I18n.t('discovery.feedbackForm.conditionSatisfactory')}
                fieldName="conditionSatisfactory"
                inputName={`conditionSatisfactory-${this.props.orderUuid}`}
                value="true"
                updateField={this.updateCondition}
                checked={this.state.conditionSatisfactory === true}
                required
              />
            </div>
            <div className="d-inline-block">
              <FormGroupWithRadio
                label={I18n.t('discovery.feedbackForm.conditionNotSatisfactory')}
                fieldName="conditionSatisfactory"
                inputName={`conditionNotSatisfactory-${this.props.orderUuid}`}
                value="false"
                updateField={this.updateCondition}
                checked={this.state.conditionSatisfactory === false}
                required
              />
            </div>
          </FormGroup>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div>
        {this.showStructuredFeedback() && this.renderStructuredFeedback()}
        <FormGroup
          label={I18n.t('discovery.feedbackForm.rateTransaction')}
        >
          <RCStarRatingSelector
            onSelect={this.starRatingSelected}
            rating={this.state.rating}
            inputName={`feedback-star-rating-${this.props.orderUuid}`}
          />
        </FormGroup>
        {this.showStartAConversation() && this.renderStartAConversation()}
        <FormGroupWithInput
          inputName="message"
          id={`message-${this.props.orderUuid}`}
          inputType="textarea"
          label={I18n.t('discovery.feedbackForm.feedbackLabel')}
          updateField={this.onValueChange}
          value={this.state.message}
          maxLength={500}
          displayCharacterCount
          helpText={
            this.state.message ?
              I18n.t('discovery.feedbackForm.feedbackPublicHint') : ' '
          }
        />
        <button
          className="button"
          onClick={this.createFeedback}
          disabled={this.state.submitDisabled || !this.formValid()}
        >
          {I18n.t('discovery.feedbackForm.sendFeedback')}
        </button>
      </div>
    );
  }
}

export default flowRight([
  updateFeedbackMutation,
])(withUserContext(FeedbackForm)) as React.ComponentClass<IOwnProps>;
